import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Alert, Row, Col, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import { STRIPE_PAYMENTS_ENABLED } from '../featureFlags';

// Stripe flat processing fee (3%)
const STRIPE_FEE_RATE = 0.03;

const formatCurrency = (amount) =>
    new Intl.NumberFormat(navigator.language || 'en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(Number(amount ?? 0));

// PWA origin used for Zettle x-callback-url return URLs.
const PWA_ORIGIN = 'https://gemrxdoctors.com';

/**
 * CollectPaymentModal
 *
 * Props:
 *   show                – boolean
 *   balanceDue          – Float
 *   transactionId       – string  (passed to Zettle deep-link as "reference")
 *   ticketType          – string  ("sale" | "repair")
 *   onClose             – () => void
 *   onSubmit            – async (payload) => void        (Cash / Card manual)
 *   onStripeCheckout    – async (amount, passCcFees) => string  (returns checkoutUrl)
 *   onZettleCheckout    – async (amount) => { pendingId }       (creates pending record)
 *   onPollPayments      – async () => PaymentBean[]      (fetches latest payments)
 *   initialPaymentCount – number                         (payments.length at open time)
 */
export const CollectPaymentModal = ({
    show,
    balanceDue,
    transactionId,
    ticketType,
    onClose,
    onSubmit,
    onStripeCheckout,
    onZettleCheckout,
    onPollPayments,
    initialPaymentCount = 0,
}) => {
    const { t } = useTranslation();

    const [amount, setAmount]                  = useState('');
    const [paymentMethod, setPaymentMethod]    = useState('Cash');
    const [lastFour, setLastFour]              = useState('');
    const [authorizationNumber, setAuthNumber] = useState('');
    const [zelleReference, setZelleReference]  = useState('');
    const [ccFeeApplied, setCcFeeApplied]      = useState(false);
    const [error, setError]                    = useState(null);
    const [isSubmitting, setIsSubmitting]      = useState(false);

    // ── Stripe QR state ───────────────────────────────────────────────────────
    const [qrUrl, setQrUrl]                             = useState(null);
    const [isWaitingForPayment, setIsWaitingForPayment] = useState(false);
    const [paymentDetected, setPaymentDetected]         = useState(false);

    const pollIntervalRef        = useRef(null);
    const initialPaymentCountRef = useRef(0);

    // ── Reset whenever the modal opens / closes ───────────────────────────────
    useEffect(() => {
        if (show) {
            setAmount(balanceDue != null ? parseFloat(balanceDue).toFixed(2) : '');
            setPaymentMethod('Cash');
            setLastFour('');
            setAuthNumber('');
            setZelleReference('');
            setCcFeeApplied(false);
            setError(null);
            setQrUrl(null);
            setIsWaitingForPayment(false);
            setPaymentDetected(false);
        } else {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }
        }
    }, [show, balanceDue]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        };
    }, []);

    const isCardMethod      = paymentMethod === 'Card';
    const isStripeMethod    = paymentMethod === 'Stripe';
    const isZelleMethod     = paymentMethod === 'Zelle';
    const isPayPalPOSMethod = paymentMethod === 'PayPalPOS';
    const parsedAmount   = parseFloat(amount) || 0;

    const ccFeeAmount = (isStripeMethod || isCardMethod) && ccFeeApplied
        ? parseFloat((parsedAmount * STRIPE_FEE_RATE).toFixed(2))
        : 0;
    const totalCharge = parseFloat((parsedAmount + ccFeeAmount).toFixed(2));

    // ── PayPal POS: register pending record, then deep-link to Zettle app ────
    const handlePayPalPOSGenerate = async () => {
        if (!onZettleCheckout) {
            setError(t('PayPal POS checkout is not configured'));
            return;
        }
        if (parsedAmount <= 0) {
            setError(t('Please enter a valid payment amount'));
            return;
        }
        setIsSubmitting(true);
        setError(null);
        try {
            // 1. Create the pending record so the webhook can correlate the payment.
            await onZettleCheckout(parsedAmount);

            initialPaymentCountRef.current = initialPaymentCount;

            // 2. Try to deep-link into the Zettle iOS app.
            //    The "reference" field is echoed back in the PurchaseCreated Pusher
            //    webhook payload, enabling exact transactionId matching on the backend.
            if (transactionId) {
                const amountInCents = Math.round(parsedAmount * 100);
                const returnPath    = `/transactions/${ticketType || 'sale'}/${transactionId}`;
                const params = new URLSearchParams({
                    amount:      amountInCents,
                    currency:    'USD',
                    reference:   transactionId,
                    'x-success': `${PWA_ORIGIN}${returnPath}?zettle=success`,
                    'x-cancel':  `${PWA_ORIGIN}${returnPath}?zettle=cancel`,
                });
                window.location.href = `zettle://x-callback-url/payment/v1?${params.toString()}`;
            }

            // 3. Fallback: if the deep-link doesn't fire (Zettle not installed, or
            //    running in a non-iOS context), show the existing "waiting on terminal"
            //    polling UI after a short delay so the employee can still process
            //    the payment manually on the physical device.
            setTimeout(() => {
                if (!paymentDetected) {
                    setIsWaitingForPayment(true);
                    pollIntervalRef.current = setInterval(async () => {
                        try {
                            const latest = await onPollPayments();
                            if (Array.isArray(latest) && latest.length > initialPaymentCountRef.current) {
                                clearInterval(pollIntervalRef.current);
                                pollIntervalRef.current = null;
                                setPaymentDetected(true);
                                setIsWaitingForPayment(false);
                                setTimeout(() => onClose(), 2500);
                            }
                        } catch (e) {
                            console.error('PayPal POS poll error:', e);
                        }
                    }, 3000);
                }
            }, 1500);

        } catch (e) {
            setError(e?.response?.data || e?.message || t('Failed to register PayPal POS payment'));
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── Stripe: generate QR + start polling ──────────────────────────────────
    const handleStripeGenerate = async () => {
        if (!onStripeCheckout) {
            setError(t('Stripe checkout is not configured'));
            return;
        }
        if (parsedAmount <= 0) {
            setError(t('Please enter a valid payment amount'));
            return;
        }
        setIsSubmitting(true);
        setError(null);
        try {
            const url = await onStripeCheckout(parsedAmount, ccFeeApplied);
            if (!url) {
                setError(t('No checkout URL returned from server'));
                return;
            }

            // Freeze the payment count at QR-generation time
            initialPaymentCountRef.current = initialPaymentCount;
            setQrUrl(url);
            setIsWaitingForPayment(true);

            // Poll every 3 s — detect when webhook records the payment
            pollIntervalRef.current = setInterval(async () => {
                try {
                    const latest = await onPollPayments();
                    if (Array.isArray(latest) && latest.length > initialPaymentCountRef.current) {
                        clearInterval(pollIntervalRef.current);
                        pollIntervalRef.current = null;
                        setPaymentDetected(true);
                        setIsWaitingForPayment(false);
                        setTimeout(() => onClose(), 2500);
                    }
                } catch (e) {
                    console.error('Payment poll error:', e);
                }
            }, 3000);

        } catch (e) {
            setError(e?.response?.data || e?.message || t('Failed to generate payment QR'));
        } finally {
            setIsSubmitting(false);
        }
    };

    // Cancel while QR is displayed — stop polling and close
    const handleCancelQR = () => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
        onClose();
    };

    // ── Manual payment (Cash / Card) ──────────────────────────────────────────
    const handleManualSubmit = async () => {
        setError(null);
        if (!amount || parsedAmount <= 0) {
            setError(t('Please enter a valid payment amount'));
            return;
        }
        if (isCardMethod && lastFour.length > 0 && !/^\d{4}$/.test(lastFour)) {
            setError(t('Last four digits must be exactly 4 numbers'));
            return;
        }
        setIsSubmitting(true);
        try {
            await onSubmit({
                amount:              totalCharge,
                baseAmount:          parsedAmount,
                ccFeeAmount,
                ccFeeApplied:        isCardMethod && ccFeeApplied,
                paymentMethod,
                lastFour:            isCardMethod ? lastFour || null : null,
                authorizationNumber: isCardMethod ? authorizationNumber || null : null,
                zettleReference:     isZelleMethod ? zelleReference || null : null,
            });
            onClose();
        } catch (e) {
            setError(e?.response?.data || e?.message || t('Failed to record payment'));
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Modal show={show} onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>{t('Collect Payment')}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* ── PayPal POS waiting state ─────────────────────────────── */}
                {isPayPalPOSMethod && isWaitingForPayment ? (
                    <div className="text-center py-3">
                        {paymentDetected ? (
                            <>
                                <div className="text-success mb-2" style={{ fontSize: '3.5rem' }}>✓</div>
                                <p className="fw-bold text-success fs-5 mb-1">{t('Payment received!')}</p>
                                <p className="text-muted small">{t('Closing…')}</p>
                            </>
                        ) : (
                            <>
                                <div style={{ fontSize: '2.5rem' }} className="mb-2">💳</div>
                                <p className="fw-semibold mb-1">{t('Charge the customer on the PayPal POS terminal')}</p>
                                <Alert variant="info" className="py-2 px-3 small mb-3 text-start">
                                    <div className="d-flex justify-content-between fw-bold">
                                        <span>{t('Amount to charge')}:</span>
                                        <span>{formatCurrency(parsedAmount)}</span>
                                    </div>
                                </Alert>
                                <div className="d-flex align-items-center justify-content-center text-muted small">
                                    <Spinner size="sm" animation="border" className="me-2" />
                                    {t('Waiting for PayPal POS confirmation…')}
                                </div>
                            </>
                        )}
                    </div>
                ) : (

                /* ── QR code / waiting state ────────────────────────────── */
                isStripeMethod && qrUrl ? (
                    <div className="text-center py-2">
                        {paymentDetected ? (
                            <>
                                <div className="text-success mb-2" style={{ fontSize: '3.5rem' }}>✓</div>
                                <p className="fw-bold text-success fs-5 mb-1">{t('Payment received!')}</p>
                                <p className="text-muted small">{t('Closing…')}</p>
                            </>
                        ) : (
                            <>
                                <p className="fw-semibold mb-1">
                                    {t('Ask the customer to scan this QR code')}
                                </p>
                                <p className="text-muted small mb-3">
                                    {t('They can pay with Apple Pay, Google Pay, or their card on their own device.')}
                                </p>

                                <div className="d-flex justify-content-center mb-3 p-3 bg-white rounded border">
                                    <QRCode value={qrUrl} size={210} />
                                </div>

                                <Alert variant="info" className="py-2 px-3 small mb-3 text-start">
                                    <div className="d-flex justify-content-between">
                                        <span>{t('Payment amount')}:</span>
                                        <span>{formatCurrency(parsedAmount)}</span>
                                    </div>
                                    {ccFeeApplied && (
                                        <div className="d-flex justify-content-between text-muted">
                                            <span>{t('Online platform fee (3%)')}:</span>
                                            <span>+{formatCurrency(ccFeeAmount)}</span>
                                        </div>
                                    )}
                                    <div className="d-flex justify-content-between fw-bold border-top mt-1 pt-1">
                                        <span>{t('Total charged')}:</span>
                                        <span>{formatCurrency(totalCharge)}</span>
                                    </div>
                                </Alert>

                                <div className="d-flex align-items-center justify-content-center text-muted small">
                                    <Spinner size="sm" animation="border" className="me-2" />
                                    {t('Waiting for customer to pay…')}
                                </div>
                            </>
                        )}
                    </div>
                ) : (
                    <>
                        {/* Balance due banner */}
                        <div className="mb-3 p-2 bg-light rounded d-flex justify-content-between align-items-center">
                            <span className="fw-semibold">{t('Balance Due')}</span>
                            <span className="fs-5 fw-bold text-danger">{formatCurrency(balanceDue)}</span>
                        </div>

                        {/* Amount */}
                        <Form.Group className="mb-3">
                            <Form.Label>{t('Payment Amount')}</Form.Label>
                            <Form.Control
                                type="number"
                                min="0.01"
                                step="0.01"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                placeholder="0.00"
                            />
                        </Form.Group>

                        {/* Payment method */}
                        <Form.Group className="mb-3">
                            <Form.Label>{t('Payment Method')}</Form.Label>
                            <Form.Select
                                value={paymentMethod}
                                onChange={(e) => { setPaymentMethod(e.target.value); setCcFeeApplied(false); }}
                            >
                                <option value="Cash">{t('Cash')}</option>
                                <option value="Card">{t('Card (Manual Entry)')}</option>
                                <option value="Zelle">{t('Zelle')}</option>
                                {STRIPE_PAYMENTS_ENABLED && (
                                    <option value="Stripe">{t('Stripe (Customer QR)')}</option>
                                )}
                                <option value="PayPalPOS">{t('PayPal POS (Hardware Terminal)')}</option>
                            </Form.Select>
                        </Form.Group>

                        {/* ── Card manual fields ─────────────────────────── */}
                        {isCardMethod && (
                            <>
                                <Row className="mb-3">
                                    <Col>
                                        <Form.Group>
                                            <Form.Label>{t('Last 4 Digits')}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                maxLength={4}
                                                value={lastFour}
                                                onChange={(e) => setLastFour(e.target.value.replace(/\D/g, ''))}
                                                placeholder="0000"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group>
                                            <Form.Label>{t('Authorization Number')}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={authorizationNumber}
                                                onChange={(e) => setAuthNumber(e.target.value)}
                                                placeholder={t('Auth #')}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="switch"
                                        id="cc-fee-switch-card"
                                        label={`${t('Pass platform fee to customer')} (${(STRIPE_FEE_RATE * 100).toFixed(0)}%)`}
                                        checked={ccFeeApplied}
                                        onChange={(e) => setCcFeeApplied(e.target.checked)}
                                    />
                                </Form.Group>
                            </>
                        )}

                        {/* ── Zelle reference ───────────────────────────── */}
                        {isZelleMethod && (
                            <Form.Group className="mb-3">
                                <Form.Label>{t('Zelle Confirmation Number')} <span className="text-muted fw-normal">({t('optional')})</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    value={zelleReference}
                                    onChange={(e) => setZelleReference(e.target.value)}
                                    placeholder={t('Enter Zelle confirmation # if available')}
                                />
                            </Form.Group>
                        )}

                        {/* ── Stripe QR setup ────────────────────────────── */}
                        {isStripeMethod && (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="switch"
                                        id="cc-fee-switch-stripe"
                                        label={`${t('Pass 3% online platform fee to customer')}`}
                                        checked={ccFeeApplied}
                                        onChange={(e) => setCcFeeApplied(e.target.checked)}
                                    />
                                </Form.Group>

                                <Alert variant="info" className="py-2 px-3 small mb-3">
                                    <div className="d-flex justify-content-between">
                                        <span>{t('Payment amount')}:</span>
                                        <span>{formatCurrency(parsedAmount)}</span>
                                    </div>
                                    {ccFeeApplied && (
                                        <div className="d-flex justify-content-between text-muted">
                                            <span>{t('Online platform fee (3%)')}:</span>
                                            <span>+{formatCurrency(ccFeeAmount)}</span>
                                        </div>
                                    )}
                                    <div className="d-flex justify-content-between fw-bold border-top mt-1 pt-1">
                                        <span>{t('Total charged')}:</span>
                                        <span>{formatCurrency(totalCharge)}</span>
                                    </div>
                                </Alert>

                                <p className="text-muted small mb-0">
                                    {t('Generates a QR code the customer scans on their own device. Supports Apple Pay, Google Pay, and card entry. Payment is recorded automatically.')}
                                </p>
                            </>
                        )}

                        {/* CC fee breakdown for manual Card */}
                        {isCardMethod && ccFeeApplied && parsedAmount > 0 && (
                            <div className="p-3 bg-light rounded mb-3 border">
                                <div className="d-flex justify-content-between mb-1">
                                    <span className="text-muted">{t('Base Amount')}</span>
                                    <span>{formatCurrency(parsedAmount)}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-1">
                                    <span className="text-muted">
                                        {t('Platform fee')} ({(STRIPE_FEE_RATE * 100).toFixed(0)}%)
                                    </span>
                                    <span>{formatCurrency(ccFeeAmount)}</span>
                                </div>
                                <hr className="my-2" />
                                <div className="d-flex justify-content-between fw-bold fs-5">
                                    <span>{t('Total to Charge Customer')}</span>
                                    <span className="text-primary">{formatCurrency(totalCharge)}</span>
                                </div>
                            </div>
                        )}

                        {/* ── PayPal POS setup ───────────────────────────── */}
                        {isPayPalPOSMethod && (
                            <Alert variant="secondary" className="py-2 px-3 small mb-3">
                                <div className="d-flex justify-content-between fw-bold mb-1">
                                    <span>{t('Amount to charge')}:</span>
                                    <span>{formatCurrency(parsedAmount)}</span>
                                </div>
                                <p className="mb-0 text-muted">
                                    {t('Click "Charge on PayPal POS" then process the payment on the hardware terminal. The transaction will be recorded automatically when the payment completes.')}
                                </p>
                            </Alert>
                        )}
                    </>
                )
                )}
            </Modal.Body>

            <Modal.Footer>
                {(isPayPalPOSMethod && isWaitingForPayment) ? (
                    /* POS waiting — Cancel stops polling; hidden once payment detected */
                    !paymentDetected && (
                        <Button variant="secondary" onClick={handleCancelQR}>
                            {t('Cancel')}
                        </Button>
                    )
                ) : isStripeMethod && qrUrl ? (
                    /* QR displayed — Cancel stops polling; hidden once payment detected */
                    !paymentDetected && (
                        <Button variant="secondary" onClick={handleCancelQR}>
                            {t('Cancel')}
                        </Button>
                    )
                ) : (
                    <>
                        <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
                            {t('Cancel')}
                        </Button>

                        {isStripeMethod ? (
                            <Button
                                variant="primary"
                                onClick={handleStripeGenerate}
                                disabled={isSubmitting || parsedAmount <= 0}
                            >
                                {isSubmitting
                                    ? <><Spinner size="sm" animation="border" className="me-1" />{t('Generating…')}</>
                                    : `📱 ${t('Generate QR Code')}`
                                }
                            </Button>
                        ) : isPayPalPOSMethod ? (
                            <Button
                                variant="primary"
                                onClick={handlePayPalPOSGenerate}
                                disabled={isSubmitting || parsedAmount <= 0}
                            >
                                {isSubmitting
                                    ? <><Spinner size="sm" animation="border" className="me-1" />{t('Registering…')}</>
                                    : `💳 ${t('Charge on PayPal POS')}`
                                }
                            </Button>
                        ) : (
                            <Button
                                variant="success"
                                onClick={handleManualSubmit}
                                disabled={isSubmitting || parsedAmount <= 0}
                            >
                                {isSubmitting ? t('Processing…') : t('Record Payment')}
                            </Button>
                        )}
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
};
